(() => {
  const isMobile = () => window.matchMedia('(max-width: 1024px)').matches;

  const initGroup = (group) => {
    const shell = group.querySelector('.le-launches__tabs-shell');
    const tabs = Array.from(group.querySelectorAll('[data-le-launches-tab]'));
    const panels = Array.from(group.querySelectorAll('[data-le-launches-panel]'));
    const dots = Array.from(group.querySelectorAll('[data-le-launches-dots] .le-launches__dot'));
    const track = group.querySelector('[data-le-launches-tabs-track]');
    const prevBtn = group.querySelector('[data-le-launches-tabs-prev]');
    const nextBtn = group.querySelector('[data-le-launches-tabs-next]');
    const panelsWrap = group.querySelector('[data-le-launches-panels]');

    let activeIndex = panels.findIndex((panel) => panel.classList.contains('is-active'));
    if (activeIndex < 0) activeIndex = 0;
    let syncingScroll = false;

    const syncUi = (index) => {
      activeIndex = index;

      tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      dots.forEach((dot) => {
        const i = Number.parseInt(dot.dataset.index || '0', 10);
        dot.classList.toggle('is-active', i === index);
      });
    };

    const getPanelScrollLeft = (panel) => {
      if (!panelsWrap) return 0;
      if (panel.offsetParent === panelsWrap) return panel.offsetLeft;
      const wrapRect = panelsWrap.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      return panelRect.left - wrapRect.left + panelsWrap.scrollLeft;
    };

    const getSnapLeft = (panel) => {
      const maxScroll = Math.max(0, panelsWrap.scrollWidth - panelsWrap.clientWidth);
      const side = (panelsWrap.clientWidth - panel.offsetWidth) / 2;
      const left = getPanelScrollLeft(panel) - side;
      return Math.min(Math.max(0, left), maxScroll);
    };

    const scrollPanelIntoView = (index) => {
      const panel = panels[index];
      if (!panelsWrap || !panel || !isMobile()) return;
      syncingScroll = true;
      panelsWrap.scrollTo({ left: getSnapLeft(panel), behavior: 'smooth' });
      window.setTimeout(() => {
        syncingScroll = false;
      }, 360);
    };

    const setItem = (index, { fromScroll = false } = {}) => {
      if (!Number.isFinite(index) || index < 0 || index >= panels.length) return;
      if (index === activeIndex && fromScroll) {
        syncUi(index);
        return;
      }

      syncUi(index);

      // Mobile only: keep the active pill in the horizontal tab track.
      // On PC this scrollIntoView jumps the page when keynotes sync switches groups.
      const activeTab = tabs[index];
      if (isMobile() && activeTab && track) {
        activeTab.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
      }

      if (!fromScroll) scrollPanelIntoView(index);
      updateTabsFade();
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const index = Number.parseInt(tab.dataset.index || '0', 10);
        setItem(index);
      });
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number.parseInt(dot.dataset.index || '0', 10);
        setItem(index);
      });
    });

    const tabsList = group.querySelector('[data-le-launches-tabs]');

    const updateTabsFade = () => {
      if (!track || !shell) return;

      // Measure without side padding so a centered short pill isn't treated as overflow.
      shell.classList.remove('is-tabs-scrollable');
      const contentWidth = tabsList ? tabsList.offsetWidth : track.scrollWidth;
      const canScroll = contentWidth > track.clientWidth + 8;

      shell.classList.toggle('is-tabs-scrollable', canScroll);

      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const atStart = !canScroll || track.scrollLeft <= 4;
      const atEnd = !canScroll || track.scrollLeft >= maxScroll - 4;

      shell.classList.toggle('is-tabs-at-start', atStart);
      shell.classList.toggle('is-tabs-at-end', atEnd);

      if (prevBtn) prevBtn.hidden = !canScroll || atStart;
      if (nextBtn) nextBtn.hidden = !canScroll || atEnd;
    };

    if (track) {
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          track.scrollBy({ left: -Math.max(120, track.clientWidth * 0.55), behavior: 'smooth' });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          track.scrollBy({ left: Math.max(120, track.clientWidth * 0.55), behavior: 'smooth' });
        });
      }
      track.addEventListener('scroll', updateTabsFade, { passive: true });
      window.addEventListener('resize', updateTabsFade);
      updateTabsFade();
    }

    if (panelsWrap && panels.length > 1) {
      let scrollTimer = 0;

      const syncFromPanelsScroll = () => {
        if (syncingScroll || !isMobile()) return;
        const left = panelsWrap.scrollLeft;
        let nearest = 0;
        let nearestDist = Infinity;
        panels.forEach((panel, i) => {
          const dist = Math.abs(getSnapLeft(panel) - left);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        });
        if (nearest !== activeIndex) setItem(nearest, { fromScroll: true });
        else syncUi(nearest);
      };

      panelsWrap.addEventListener(
        'scroll',
        () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(syncFromPanelsScroll, 60);
        },
        { passive: true }
      );
    }

    return { setItem };
  };

  const initLaunches = (root) => {
    if (root.dataset.leLaunchesReady === 'true') return;
    root.dataset.leLaunchesReady = 'true';

    const groups = Array.from(root.querySelectorAll('[data-le-launches-group]'));
    if (groups.length === 0) return;

    const equalizeDesktopTabWidths = () => {
      const tabLists = Array.from(root.querySelectorAll('[data-le-launches-tabs]'));
      if (tabLists.length === 0) return;

      if (isMobile()) {
        root.style.removeProperty('--le-launches-tab-width');
        return;
      }

      const groupStates = groups.map((group) => ({
        group,
        hidden: group.getAttribute('aria-hidden'),
        wasActive: group.classList.contains('is-active'),
        prevDisplay: group.style.display,
        prevVisibility: group.style.visibility,
        prevPosition: group.style.position,
        prevHeight: group.style.height,
        prevOverflow: group.style.overflow,
        prevPointerEvents: group.style.pointerEvents,
        prevWidth: group.style.width,
      }));

      root.style.removeProperty('--le-launches-tab-width');

      // Measure every group (inactive ones are display:none and otherwise report 0).
      groups.forEach((group) => {
        group.style.display = 'flex';
        group.style.visibility = 'hidden';
        group.style.position = 'absolute';
        group.style.left = '-99999px';
        group.style.top = '0';
        group.style.height = 'auto';
        group.style.overflow = 'visible';
        group.style.pointerEvents = 'none';
        group.style.width = 'max-content';
      });

      let maxWidth = 188;
      tabLists.forEach((list) => {
        maxWidth = Math.max(maxWidth, Math.ceil(list.getBoundingClientRect().width));
      });

      groupStates.forEach((state) => {
        const { group } = state;
        group.style.display = state.prevDisplay;
        group.style.visibility = state.prevVisibility;
        group.style.position = state.prevPosition;
        group.style.left = '';
        group.style.top = '';
        group.style.height = state.prevHeight;
        group.style.overflow = state.prevOverflow;
        group.style.pointerEvents = state.prevPointerEvents;
        group.style.width = state.prevWidth;
      });

      root.style.setProperty('--le-launches-tab-width', `${maxWidth}px`);
    };

    const controllers = groups.map(initGroup);
    let activeGroup = groups.findIndex((group) => group.classList.contains('is-active'));
    if (activeGroup < 0) activeGroup = 0;

    const setGroup = (index) => {
      if (!Number.isFinite(index) || index < 0 || index >= groups.length) return;
      activeGroup = index;

      groups.forEach((group, i) => {
        const isActive = i === index;
        group.classList.toggle('is-active', isActive);
        group.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      controllers[activeGroup]?.setItem(0);
    };

    const syncId = (root.dataset.syncWith || '').trim();
    const onKeynoteChange = (event) => {
      const detail = event.detail || {};
      if (syncId && detail.sectionId && detail.sectionId !== syncId) return;
      setGroup(Number(detail.index));
    };

    document.addEventListener('le:keynote-change', onKeynoteChange);
    window.addEventListener('resize', equalizeDesktopTabWidths);
    equalizeDesktopTabWidths();
    root._leLaunchesSetGroup = setGroup;
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-le-launches]').forEach(initLaunches);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
